import React, { useState, useEffect, useLayoutEffect, useContext } from "react";
import ApiLog from "../../../app/api/admin/log/ApiLog";
import Page from "../../modules/page";
import { ContextContent } from "../../modules/content/contentContext";
import Loader from "../../components/loader";
import "./styles.css";

// Страница «Логи» — 5 вкладок (node/device/redroid-scripts + rbx-health + rbx-proxy, см.
// config/logging.php на бэкенде), под каждой — выбор дня (Monolog daily-файлы, максимум
// неделя доступна — старше подчищает panel:clean-logs) и сырой текст лога со скроллом.
// Группировка: строки *-scripts несут "...Execute #N ..." (см. BaseDeviceScript/BaseSshScript/
// BaseRedroidScript::addLog) — ВСЕ строки одного N сводятся в ОДИН блок, даже если в файле они
// идут не подряд. Несмежность — обычное дело для Multi: родитель синхронно вызывает ребёнка
// (runChild), и в файл сначала целиком улетают строки ребёнка (свой N), а «хвост» родителя
// (screen-check-final/end) дописывается ПОСЛЕ, уже после чужого блока — без слияния по id
// один и тот же execute показывался бы двумя разорванными кусками. rbx-health — не про запуски
// скриптов (сама по себе диагностика, см. DetectRbxDropCommand), "#N" в строках не будет — там
// каждая строка своя одиночная группа (см. buildGroups). rbx-proxy — тоже не про запуски, но
// "#N" в его строках ЕСТЬ и означает совсем другое: id устройства и аккаунта («устройство #61
// (redroid-01) — аккаунт #126»). Сгруппировав по нему, мы свалили бы все запросы устройства в
// один блок с заголовком «#61», который на этой странице читается как номер запуска, и потеряли
// бы хронологию — а прокси-лог читают именно по времени. Поэтому у вкладки isGrouped: false и
// строки идут плоско, свежие сверху. Порядок блоков — по позиции ПОСЛЕДНЕЙ
// строки блока в файле, по убыванию (самая свежая активность наверху); ВНУТРИ блока порядок строк
// не трогаем — так шаги видно по порядку выполнения. targets — {execute_id: имя устройства/ноды}
// с бэка (LogsController), в заголовок блока. hasException — строка "exception" где-то в блоке
// (см. BaseScript::handleError — {"exception": "...", "step": ...}) — такой блок подсвечиваем:
// обычно самое интересное в логе именно это.
// isGrouped: false — не сводить строки по "#N" (см. докблок выше); по умолчанию сводим.
const TABS = [
    { key: 'node-scripts', label: 'Ноды' },
    { key: 'device-scripts', label: 'Устройства' },
    { key: 'redroid-scripts', label: 'Redroid' },
    { key: 'rbx-health', label: 'Roblox' },
    { key: 'rbx-proxy', label: 'Прокси', isGrouped: false },
];

const EXECUTE_ID_RE = /#(\d+)/;

// Состояние вкладки до/во время загрузки — раскладывается в набор ключей целиком, чтобы у
// рендера всегда были dates/lines/targets массивами/объектами.
const EMPTY = { dates: [], date: null, lines: [], targets: {}, loading: true };

// Строки → блоки по "#N" (см. докблок выше) — сливаем ВСЕ вхождения одного N в один блок,
// строка без "#N" — всегда своя отдельная одиночная группа (ключ на основе индекса, ни с чем
// не сливается). Возвращает уже отсортированным: свежее (по последней строке блока) — первым.
//
// isGrouped: false — канал, где "#N" не номер запуска (rbx-proxy): каждая строка сама по себе,
// заголовков блоков нет, порядок чисто хронологический.
function buildGroups(lines, isGrouped = true) {
    const byKey = new Map();
    const groups = [];
    lines.forEach((line, index) => {
        const match = isGrouped ? line.match(EXECUTE_ID_RE) : null;
        const id = match ? match[1] : null;
        const mapKey = id !== null ? id : `_line${index}`;
        let group = byKey.get(mapKey);
        if (!group) {
            group = { key: `g${mapKey}`, id, lines: [], hasException: false, lastIndex: index };
            byKey.set(mapKey, group);
            groups.push(group);
        }
        group.lines.push({ key: index, text: line });
        group.lastIndex = index;
        if (/exception/i.test(line)) group.hasException = true;
    });
    return [...groups].sort((a, b) => b.lastIndex - a.lastIndex);
}

export default function LogsIndex({ ...other }) {
    return <Page {...other}>
        <LogsIndexContent/>
    </Page>;
}

function LogsIndexContent() {
    const contextContent = useContext(ContextContent);
    const [_tab, set_tab] = useState(TABS[0].key);
    // На вкладку: {dates, date, lines, loading} — не сбрасываем при переключении вкладок
    // туда-обратно (уже загруженное остаётся в памяти).
    const [_state, set_state] = useState({});

    useLayoutEffect(() => {
        contextContent.setTitle('Логи');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const load = async (channel, date) => {
        // EMPTY обязателен и здесь: без него вкладка на время загрузки оставалась объектом
        // {loading:true} без dates/lines, а ниже `_state[_tab] ?? EMPTY` такой объект уже не
        // подменял дефолтом — рендер падал на current.dates.length ещё до прихода ответа,
        // т.е. страница крашилась при каждом открытии.
        set_state((prev) => ({ ...prev, [channel]: { ...EMPTY, ...(prev[channel] ?? {}), loading: true } }));
        const data = await ApiLog.Index(channel, date).getResponse();
        set_state((prev) => ({
            ...prev,
            [channel]: {
                dates: data?.dates ?? [],
                date: data?.date ?? null,
                lines: data?.lines ?? [],
                targets: data?.targets ?? {},
                loading: false,
            },
        }));
    };

    // Ленивая загрузка вкладки при первом открытии (последний доступный день — date не задан).
    useEffect(() => {
        if (!(_tab in _state)) load(_tab, '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [_tab]);

    const current = _state[_tab] ?? EMPTY;

    // Сортировка (свежее — первым) уже внутри buildGroups().
    const groups = buildGroups(current.lines, TABS.find((tab) => tab.key === _tab)?.isGrouped !== false);

    return <>
        <div className="logs__tabs">
            {TABS.map((tab) => (
                <button
                    key={tab.key}
                    type="button"
                    className={`logs__tab${_tab === tab.key ? ' logs__tab_active' : ''}`}
                    onClick={() => set_tab(tab.key)}
                >
                    {tab.label}
                </button>
            ))}
        </div>

        {current.dates.length > 0 ? (
            <div className="logs__dates">
                {current.dates.map((date) => (
                    <button
                        key={date}
                        type="button"
                        className={`logs__date${current.date === date ? ' logs__date_active' : ''}`}
                        onClick={() => load(_tab, date)}
                    >
                        {date}
                    </button>
                ))}
            </div>
        ) : null}

        {current.loading ? (
            <Loader/>
        ) : current.lines.length === 0 ? (
            <div className="logs__empty">{current.dates.length === 0 ? 'Логов пока нет' : 'Файл пуст'}</div>
        ) : (
            <div className="logs__view">
                {groups.map((group) => (
                    <div key={group.key} className={`logs__group${group.hasException ? ' logs__group_exception' : ''}`}>
                        {group.id !== null ? (
                            <div className="logs__group-header">
                                #{group.id}{current.targets[group.id] ? ` — ${current.targets[group.id]}` : ''}
                            </div>
                        ) : null}
                        {group.lines.map((line) => (
                            <div key={line.key} className="logs__line">{line.text}</div>
                        ))}
                    </div>
                ))}
            </div>
        )}
    </>;
}
